/* eslint-disable max-len */
/* eslint-disable jsx-a11y/alt-text */
import React, { useEffect, useState } from 'react';

import { POSCatalogData, POSCatalogModelData } from '../../../common/libraries/modelViewer/Data/POSCatalogData';
// import { Currencies } from '../../Util/currencies';
import { CartItem, CartItemHelper } from '../../Data/CartItem';
import { useAppSelector } from '../../../Redux/Store/hooks';

import { ComponentOrderCustomAttributes } from '../Components/Specific/CustomAttributes/OrderCustomAttributes';

import './OrderView.css';
import { CartItemHeader } from '../Components/Specific/ShoppingCart/CartItemHeader';
import { CartItemThumbnail } from '../Components/Specific/ShoppingCart/CartItemThumbnail';
import { CartItemDetails } from '../Components/Specific/ShoppingCart/CartItemDetails';
import { CatalogFileCustomAttributesDefinition } from '../../../common/libraries/fileFormats/catalog/catalog';
import { CartOrderTotal } from '../Components/Specific/ShoppingCart/CartOrderTotal';
import { CustomAttributeData } from '../../../common/libraries/customAttributes/customAttributes';

import imgDownload from '../../../Resources/Images/download.svg';

type OrderViewParameters = {
  catalog : POSCatalogData,
  cartItems : CartItem[],
  changeQuantityHandler : (cartItem: CartItem, newQuantity: number) => void,
  removeItemHandler : (cartItem: CartItem) => void,
  clearCartHandler : () => void,
  createOrderDocumentHandler : () => Promise<void>,
  downloadOrderPreview: () => Promise<void>,
  customOrderLineAttributesData : CatalogFileCustomAttributesDefinition | undefined,
  lang: string,
};

export function OrderView({
  catalog,
  cartItems,
  changeQuantityHandler,
  removeItemHandler,
  clearCartHandler,
  createOrderDocumentHandler,
  downloadOrderPreview,
  customOrderLineAttributesData,
  lang,
} : OrderViewParameters) {

  const [globalShowPrices, setGlobalShowPrices] = useState<boolean>(false);

  useEffect(() => {
    const root = document.documentElement;
    const computedStyle = getComputedStyle(root);

    const showPricesValue = computedStyle.getPropertyValue('--show-prices').trim();
    setGlobalShowPrices(showPricesValue === 'true');
  }, []);

  // From the store, get the strings
  const strings = useAppSelector((state) => {
    return state.TextsReducer.strings;
  })!;

  const attributes = useAppSelector((state) => {
    return state.CustomAttributesReducer.customAttributesValues;
  })!;

  // Controls variables to display the items correctly
  let lastCollectionInserted = '';
  let lastObjectInserted = '';

  const [canCheckout, setCanCheckout] = useState(true);

  useEffect(() => {
    let isCheckoutActive = true;
    catalog?.catalogFileInfo?.orderCustomAttributesDefinitions.attributes.forEach((att) => {
      if (att.isMandatory) {
        const savedAttribute = attributes.find((satt) => satt.attributeKey === att.key);
        // eslint-disable-next-line max-len
        if (savedAttribute?.attributeKey === undefined || savedAttribute?.attributeValue === undefined || savedAttribute?.attributeValue === '') {
          isCheckoutActive = false;
        }
      }
    });

    setCanCheckout(isCheckoutActive);
  }, [attributes]);

  const renderCartItem = (cartItem : CartItem, index : number): JSX.Element => {
    const itemCollectionName = CartItemHelper.GetCollectionName(cartItem);
    const itemName = CartItemHelper.GetObjectName(cartItem);

    let addCollectionInformation = false;

    if (itemCollectionName !== lastCollectionInserted) {
      lastCollectionInserted = itemCollectionName;
      addCollectionInformation = true;
    }

    if (addCollectionInformation || itemName !== lastObjectInserted) {
      lastObjectInserted = itemName;
    }

    if (catalog === undefined) {
      return <div className="empty" />;
    }

    const catalogObject = catalog.modelsData.get(cartItem.catalogModelKey);

    let itemPriceCurrency = 0;
    if (catalogObject && catalogObject.catalogData && catalogObject.data) {
      itemPriceCurrency = catalogObject.catalogData.priceCurrency;
    }

    // Check we this is an predefined variant
    if (catalogObject === undefined
      || catalogObject.data === undefined
      || catalogObject.data.variants === undefined
      || catalogObject.catalogData === undefined
    ) {
      return <div className="empty" />;
    }
    let itemVariant = POSCatalogModelData.GetVariantDescription(
      CartItemHelper.GetVariant(cartItem),
      catalogObject.data.variants,
    );

    if (itemVariant === '') {
      itemVariant = strings.CUSTOMIZED_VARIANT;
    }

    return (
      <div style={{ width: '100%' }} key={index}>
        <div
          key={`a+${index}`}
          style={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <CartItemHeader
            articleName={itemName}
            articleVariant={itemVariant}
            itemQuantity={CartItemHelper.GetQuantity(cartItem)}
            onQuantityChange={changeQuantityHandler}
            onRemoveItem={removeItemHandler}
            itemPrice={cartItem.totalPrice}
            priceCurrency={itemPriceCurrency}
            cartItem={cartItem}
          />
        </div>

        {/* Item Main Info */}
        <div
          key={`b+${index}`}
          className="cart-order-main-info"
        >
          <div
            key={`aa+${index}`}
            className="cart-order-main-info-item-a"
          >
            <CartItemThumbnail
              catalogObject={catalogObject}
              articleVariant={itemVariant}
            />
          </div>

          {/* Item composition */}
          <div
            key={`ab+${index}`}
            className="cart-order-main-info-item-b"
          >
            <CartItemDetails
              catalogObject={catalogObject}
              cartItem={cartItem}
              cartItemIndex={index}
              customOrderLineAttributesData={customOrderLineAttributesData}
              priceData={catalog.catalogFileInfo?.priceData}
              tagsTranslation={catalog.catalogFileInfo?.tagsTranslation}
            />
          </div>
        </div>
      </div>
    );
  };

  const catalogObject = catalog?.modelsData.get(cartItems[0].catalogModelKey);

  let itemPriceCurrency = 0;
  if (catalogObject && catalogObject.catalogData && catalogObject.data) {
    itemPriceCurrency = catalogObject.catalogData.priceCurrency;
  }

  let totalCostOfAllItems = cartItems.reduce((total, curCartItem) => {
    return total + Number(CartItemHelper.GetQuantity(curCartItem)) * Number(curCartItem.totalPrice) + Number(curCartItem.extrasPrice);
  }, 0);

  // From the store, get the custom attributes values
  const customAttributesValues = useAppSelector((store) => {
    return store.CustomAttributesReducer.customAttributesValues;
  });

  let totalOrderCustomAttrPrice = 0.0;
  if (
    catalog.catalogFileInfo !== undefined
    && catalog.catalogFileInfo.orderCustomAttributesDefinitions !== undefined
    && catalog.catalogFileInfo.orderCustomAttributesDefinitions.attributes !== undefined
  ) {
    totalOrderCustomAttrPrice = CustomAttributeData.calculateAttributesTotalPrice(catalog.catalogFileInfo.orderCustomAttributesDefinitions.attributes, customAttributesValues, undefined, undefined, catalog.catalogFileInfo.priceData);

  }
  totalCostOfAllItems += totalOrderCustomAttrPrice;

  return (
    <div
      className="product-summary-background"
      style={{
        height: '100%', display: 'flex', flexDirection: 'column', alignContent: 'center',
      }}
    >
      <div className="more-product-summary-background" style={{ width: '80%', margin: 'auto' }}>
        <h1>{strings.PRODUCT_LIST_ORDER_LABEL}</h1>
        { globalShowPrices
          ? (
            <CartOrderTotal
              totalPrice={totalCostOfAllItems}
              priceCurrency={itemPriceCurrency}
            />
          ) : (
            <div />
          )}
        <div
          key="item-cust-atts"
          className="container-x"
          style={
            {
              alignContent: 'center',
              justifyContent: 'center',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }
          }
        >
          {cartItems.map((cartItem, index) => {
            return renderCartItem(cartItem, index);
          })}
          { catalog !== undefined && (
            <ComponentOrderCustomAttributes
              catalog={catalog}
              priceInfo={itemPriceCurrency}
              priceData={catalog.catalogFileInfo?.priceData}
              lang={lang}
            />
          )}

          {/* Footer */}

          {cartItems.length !== 0 && (
          <div
            style={{
              borderTop: '2px solid #000000',
            }}
          >
            <div style={{ marginTop: '30px', paddingBottom: '100px' }}>
              <div
                style={{
                  textAlign: 'left',
                  width: '30%',
                  display: 'inline-block',
                }}
              >
                <button
                  type="button"
                  disabled={!canCheckout}
                  className="btn btn-secondary rounded"
                  onClick={() => {
                    createOrderDocumentHandler();
                  }}
                >
                  {strings.CHECKOUT_ORDER_BUTTON}
                </button>
              </div>
              <div
                style={{
                  textAlign: 'left',
                  width: '20%',
                  display: 'inline-block',
                }}
              >
                <button
                  type="button"
                  disabled={!canCheckout}
                  className="btn btn-secondary rounded"
                  style={{ border: 'none', background: 'white' }}
                  onClick={() => {
                    downloadOrderPreview();
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    padding: '3px',
                  }}
                  >
                    <img
                      src={imgDownload}
                      alt="Download Icon"
                      style={{ paddingRight: '0px' }}
                    />
                    <div
                      style={{ color: 'black', background: 'white' }}
                    >
                      {strings.PREVIEW_ORDER_BUTTON}
                    </div>
                  </div>
                </button>
              </div>

              <div
                style={{
                  textAlign: 'right',
                  width: '50%',
                  display: 'inline-block',
                }}
              >
                <button
                  type="button"
                  className="btn btn-danger rounded"
                  onClick={() => {
                    clearCartHandler();
                    window.location.reload();
                  }}
                >
                  {strings.CLEAR_CART}
                </button>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
